<template>
	<div>
		<!-- Volet de saisie de règlement. Il ne masque pas le tableau : la liste des règlements déjà
		     imputés est justement ce qu'on veut avoir sous les yeux en en saisissant un nouveau. La
		     marge n'est posée qu'à l'ouverture, le panneau occupant une place nulle le reste du temps. -->
		<shutter-panel ref="shutterPanel" :class="{ 'mb-4': shutter_open }" />

		<div class="box invoice-supplier-payment">
			<LoadingSpinner v-if="!loaded" />

			<template v-else>
				<div class="row invoice-supplier-payment__summary">
					<div class="col-12 col-md-4">
						<div class="invoice-supplier-payment__label">{{ $t('invoice.suppliersocr_ttc') }}</div>
						<div>{{ amounts.ttc }}</div>
					</div>
					<div class="col-12 col-md-4">
						<div class="invoice-supplier-payment__label">{{ $t('invoice.supplier_payment_settled') }}</div>
						<div>{{ amounts.settled }}</div>
					</div>
					<div class="col-12 col-md-4">
						<div class="invoice-supplier-payment__label">{{ $t('invoice.supplier_payment_balance') }}</div>
						<div :class="{ 'text-success': is_settled }">{{ amounts.balance }}</div>
					</div>
				</div>

				<button class="btn btn-primary mb-4" @click="openAddPayment">
					<font-awesome-icon :icon="['fal', 'plus']" /> {{ $t('invoice.supplier_payment_add') }}
				</button>

				<!-- Mouvements bancaires qui pourraient être ce règlement. Rien n'est rapproché d'office :
				     l'encart le dit, et chaque ligne demande un geste. -->
				<div v-if="suggestions.length" class="invoice-supplier-payment__suggestions mb-4">
					<h4>
						{{ $t('invoice.supplier_bank_suggestion_title') }}
						<b-badge pill variant="primary" class="ml-1">{{ suggestions.length }}</b-badge>
					</h4>
					<p class="invoice-supplier-payment__label">{{ $t('invoice.supplier_bank_suggestion_hint') }}</p>

					<div v-for="suggestion in suggestions" :key="suggestion.bridgetransaction_id" class="row no-gutters align-items-center invoice-supplier-payment__suggestion">
						<div class="col-12 col-md-2">{{ suggestion.bridgetransaction_date_format }}</div>
						<div class="col-12 col-md-3">
							<!-- Le logo est servi par le CDN de la banque : le référent est tu, l'URL d'un
							     écran de comptabilité n'ayant pas à sortir avec la requête. -->
							<img v-if="suggestion.bank_logo_url" :src="suggestion.bank_logo_url" class="mr-1" referrerpolicy="no-referrer" style="height: 20px;">{{ suggestion.bank_name }}
							<!-- Une même banque peut porter plusieurs comptes : le nom dit sur lequel le
							     mouvement est passé. -->
							<div v-if="suggestion.account_name" class="invoice-supplier-payment__label">{{ suggestion.account_name }}</div>
						</div>
						<div class="col-12 col-md-3">
							<span
								v-for="(part, index) in describe(suggestion.bridgetransaction_description)"
								:key="index"
								:class="{ 'text-success font-weight-bold': part.match }"
							>{{ part.text }}</span>
						</div>
						<div class="col-12 col-md-2 text-md-right font-weight-bold">{{ suggestion.amount_format }}</div>
						<div class="col-12 col-md-2 text-md-right">
							<button class="btn btn-secondary" :disabled="!!assigning" @click="assign(suggestion)">
								{{ $t('invoice.supplier_bank_suggestion_assign') }}
								<font-awesome-icon v-if="assigning === suggestion.bridgetransaction_id" :icon="['fal', 'spinner']" spin />
							</button>
						</div>
					</div>
				</div>

				<p v-if="!payments.length" class="mb-0">{{ $t('invoice.supplier_payment_empty') }}</p>

				<div v-else class="table-responsive">
					<table class="table mb-0">
						<thead>
							<tr>
								<th>{{ $t('invoice.supplier_payment_date') }}</th>
								<th>{{ $t('invoice.supplier_payment_method') }}</th>
								<th>{{ $t('invoice.supplier_payment_reference') }}</th>
								<th class="text-right">{{ $t('invoice.supplier_payment_allocated') }}</th>
								<th class="text-right">{{ $t('invoice.supplier_payment_amount') }}</th>
								<th>{{ $t('invoice.supplier_payment_user') }}</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="payment in payments" :key="payment.ispayment_id">
								<td>
									{{ payment.ispayment_date_format }}
									<b-badge v-if="payment.ispayment_unpaid" variant="danger" class="ml-1 font-weight-normal">
										{{ $t('invoice.supplier_payment_unpaid') }}
									</b-badge>
								</td>
								<td>{{ payment.method ? payment.method.paymentmethod_label : '' }}</td>
								<td>
									{{ payment.ispayment_bankcheck }}
									<div v-if="payment.ispayment_comment" class="invoice-supplier-payment__label">{{ payment.ispayment_comment }}</div>

									<!-- Mouvement bancaire rapproché. C'est lui qui atteste que l'argent est
									     sorti du compte, là où le règlement seul ne dit que ce qui a été saisi. -->
									<div v-if="payment.bank_transaction" class="invoice-supplier-payment__matched">
										<b-badge variant="success" class="font-weight-normal">
											<font-awesome-icon :icon="['fal', 'link']" /> {{ $t('invoice.supplier_payment_matched') }}
										</b-badge>
										<div class="invoice-supplier-payment__label">
											<img v-if="payment.bank_transaction.bank_logo_url" :src="payment.bank_transaction.bank_logo_url" class="mr-1" referrerpolicy="no-referrer">{{ payment.bank_transaction.bank_name }}
											<template v-if="payment.bank_transaction.account_name"> — {{ payment.bank_transaction.account_name }}</template>
										</div>
										<div class="invoice-supplier-payment__label">
											{{ payment.bank_transaction.bridgetransaction_date_format }} — {{ payment.bank_transaction.bridgetransaction_description }}
										</div>
									</div>
								</td>
								<td class="text-right font-weight-bold">{{ payment.allocated_format }}</td>
								<td class="text-right">
									{{ payment.amount_format }}
									<div v-if="payment.invoices && payment.invoices.length > 1" class="invoice-supplier-payment__label">
										{{ $t('invoice.supplier_payment_shared', { nb: payment.invoices.length }) }}
									</div>
								</td>
								<td>{{ payment.ispayment_username }}</td>
								<td class="text-right">
									<a href="#" class="text-danger" @click.prevent="askDelete(payment)">
										<font-awesome-icon :icon="['fal', 'trash-alt']" />
									</a>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</template>
		</div>

		<b-modal ref="modal_delete" hide-footer>
			<template v-slot:modal-title>{{ $t('invoice.supplier_payment_delete') }}</template>

			<p>{{ $t('invoice.supplier_payment_delete_confirm') }}</p>

			<div v-if="pending_delete && pending_delete.invoices.length > 1" class="alert alert-warning">
				{{ $t('invoice.supplier_payment_delete_shared', { nb: pending_delete.invoices.length }) }}
			</div>

			<div class="text-center">
				<button class="btn btn-danger" :disabled="processing" @click="confirmDelete">
					{{ $t('invoice.supplier_payment_delete') }}
					<font-awesome-icon v-if="processing" :icon="['fal', 'spinner']" spin />
				</button>
			</div>
		</b-modal>
	</div>
</template>


<script type="text/javascript">
	import Invoice from "@/mixins/Invoice.js"
	import OpenBank from "@/mixins/OpenBank.js"
	import Payment from "@/mixins/Payment.js"
	import Shutter from "@/mixins/Shutter.js"
	import ShutterInvoice from "@/mixins/shutters-manager/Invoice.js"

	export default {
		name: "InvoiceSupplierFichePayment",

		mixins: [Invoice, Payment, OpenBank, Shutter, ShutterInvoice],

		props: {
			invoice: { type: Object, default: () => ({}) },
			invoicesupplier_id: { type: [String, Number], default: null }
		},

		data() {
			return {
				loaded: false,
				shutter_open: false,
				processing: false,
				payments: [],
				// Flux bancaires proposés au rapprochement, et celui dont l'association est en cours.
				suggestions: [],
				assigning: null,
				pending_delete: null,
				amounts: {
					ttc: '',
					settled: '',
					balance: ''
				}
			}
		},

		computed: {
			// Le solde de la facture fait foi : il est tenu par les échéances, que la saisie d'un
			// règlement met à jour.
			is_settled() {
				return this.invoice.suppliersocr_balance === 0
			}
		},

		methods: {
			/**
			 * Charge les règlements et prépare les montants. Le formatage dépend de la devise, chargée
			 * de façon asynchrone : il ne peut pas vivre dans un computed, qui rendrait des promesses.
			 */
			async init_component() {
				this.loaded = false

				const currency = this.invoice.suppliersocr_currency || 'EUR'
				const payments = await this.getInvoiceSupplierPayments(this.invoice.suppliersocr_id)

				for(const payment of payments) {
					payment.allocated_format = await this.priceFormat(payment.ispayment_allocated, currency, true)
					payment.amount_format = await this.priceFormat(payment.ispayment_amount, currency, true)
				}

				this.payments = payments

				await this.loadSuggestions(currency)

				const settled = this.invoice.suppliersocr_ttc - this.invoice.suppliersocr_balance

				this.amounts = {
					ttc: await this.priceFormat(this.invoice.suppliersocr_ttc, currency, true),
					settled: await this.priceFormat(settled, currency, true),
					balance: await this.priceFormat(this.invoice.suppliersocr_balance, currency, true)
				}

				this.loaded = true
			},

			/**
			 * Mouvements bancaires susceptibles d'être ce règlement. Une facture soldée n'en appelle
			 * aucun : le serveur le sait, mais l'écran s'épargne l'appel.
			 */
			async loadSuggestions(currency) {
				this.suggestions = []

				if(this.is_settled) {
					return
				}

				const suggestions = await this.getInvoiceSupplierBankSuggestions(this.invoice.suppliersocr_id)

				for(const suggestion of suggestions) {
					suggestion.amount_format = await this.priceFormat(suggestion.bridgetransaction_amount, currency, true)
				}

				this.suggestions = suggestions
			},

			/**
			 * Découpe le libellé de la banque autour du numéro de facture, pour en souligner la
			 * mention : c'est elle qui fait du rapprochement une quasi-certitude, là où la seule
			 * proximité des dates ne vaut qu'indice.
			 *
			 * Le découpage remplace un v-html : le libellé vient de la banque, et on ne lui confie pas
			 * le soin d'écrire des balises dans notre page.
			 *
			 * @return {Array} Segments {text, match}, le second seul étant surligné.
			 */
			describe(description) {
				const num = (this.invoice.suppliersocr_num || '').trim()
				const index = num ? (description || '').toLowerCase().indexOf(num.toLowerCase()) : -1

				if(index === -1) {
					return [{ text: description, match: false }]
				}

				return [
					{ text: description.slice(0, index), match: false },
					{ text: description.slice(index, index + num.length), match: true },
					{ text: description.slice(index + num.length), match: false },
				].filter(part => part.text)
			},

			/**
			 * Rattache le flux retenu à la facture. Le règlement est créé par la même voie que depuis
			 * l'écran des flux bancaires : imputation sur les échéances et, pour une facture
			 * électronique, annonce du paiement au fournisseur.
			 */
			async assign(suggestion) {
				this.assigning = suggestion.bridgetransaction_id

				await this.assignBridgeTransaction(suggestion.bridgetransaction_id, true, [this.invoice.suppliersocr_id], null, null, null, '')

				this.assigning = null
				this.successToast('toast.info_save_succes')

				// Le solde a changé : la fiche entière se refait, le tableau et les suggestions avec.
				this.$emit('invoice-updated')
			},

			/**
			 * Ouvre la saisie de règlement de l'application plutôt qu'un formulaire propre à cet écran :
			 * le choix du mode de règlement, la reprise du compte courant et l'imputation comptable y
			 * sont déjà traités, et ne doivent exister qu'à un seul endroit.
			 */
			openAddPayment() {
				this.shutter_open = true
				this.setUpPaymentInvoiceSupplier([this.invoice])
			},

			/**
			 * Rappelée par le volet de saisie une fois le règlement enregistré — le nom est celui
			 * qu'attend sa définition. Le solde de la facture a changé : la fiche est rechargée, et
			 * le rechargement du tableau suit par le watch, avec les montants à jour.
			 */
			loadInvoicesSupplier() {
				this.shutter_open = false
				this.$emit('invoice-updated')
			},

			askDelete(payment) {
				this.pending_delete = payment
				this.$refs.modal_delete.show()
			},

			/**
			 * Annule le règlement. L'opération porte sur le règlement entier, y compris les autres
			 * factures qu'il soldait : la modale le dit avant de laisser confirmer.
			 */
			async confirmDelete() {
				this.processing = true

				const result = await this.cancelPaymentSupplier([this.pending_delete.ispayment_id])

				this.processing = false

				if(result === null) {
					return
				}

				this.$refs.modal_delete.hide()
				this.successToast('toast.info_modif_succes')
				this.$emit('invoice-updated')
			}
		},

		watch: {
			// La fiche recharge la facture après chaque règlement : le solde et la liste se refont
			// alors ensemble, à partir des mêmes données.
			invoice: 'init_component'
		},

		mounted() {
			// Arrivée depuis le contrôle, l'approbation à peine posée : la facture vient d'être arrêtée
			// et la saisie s'ouvre d'elle-même, c'est le geste demandé en venant ici. Le garde ci-dessous
			// est alors sans objet — la fiche n'a peut-être pas fini de recharger la facture, et son
			// ancien état ferait rebondir l'utilisateur sur l'aperçu.
			const from_control = !!this.$route.query.add

			// Tant que la facture n'est entrée dans aucun circuit — ni contrôlée chez nous, ni prise en
			// charge auprès du fournisseur — ses montants ne sont pas arrêtés : rien à régler, et
			// l'onglet est grisé. Son adresse, elle, reste atteignable.
			const is_checked = this.invoice.suppliersocr_ocr_status === 'checked' || this.invoice.suppliersocr_acknowledged

			if(!from_control && !is_checked) {
				this.$router.replace({ name: 'invoiceSupplierFicheOverview', params: { invoicesupplier_id: this.invoice.suppliersocr_id } })
				return
			}

			this.init_component()

			if(from_control) {
				this.openAddPayment()
			}
		},

		components: {
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35')
		}
}
</script>
